"use client";

import { useEffect } from "react";
import { useSearchParams, usePathname } from "next/navigation";

interface TokenUrlSyncProps {
  apiToken?: string;
}

/**
 * Client component that syncs the URL with the token value from the API
 * when no token search param is present. This ensures the personalized page
 * can be shared with the same company data.
 */
export function TokenUrlSync({ apiToken }: TokenUrlSyncProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  useEffect(() => {
    const currentToken = searchParams.get("token");

    // Only update URL if:
    // 1. No token search param exists in the URL
    // 2. API returned a token value
    if (!currentToken && apiToken) {
      // Preserve existing search params and add token
      const params = new URLSearchParams(searchParams.toString());
      params.set("token", apiToken);

      // Shallow URL update (no soft navigation). Using router.replace() here
      // re-renders the route and breaks the hero Marketo form on personalized
      // pages; changing the URL without a re-render keeps the form intact while
      // still making the token shareable.
      window.history.replaceState(
        window.history.state,
        "",
        `${pathname}?${params.toString()}`,
      );
    }
  }, [apiToken, searchParams, pathname]);

  // This component doesn't render anything
  return null;
}
